import { useEffect, useRef, useState, type FormEvent } from "react";

import type {
  FocusBlockSnapshot,
  FocusExtensionMinutes,
} from "@vsta/contracts";

export type FocusBlockConnectivity =
  "connected" | "reconnecting" | "pending" | "offline" | "conflict";

export type FocusBlockUiCommand =
  | { intent: "pause" }
  | { intent: "resume" }
  | { intent: "extend"; extensionMinutes: FocusExtensionMinutes }
  | {
      intent: "reconcile-gap";
      resolution: "keep" | "unsure";
    }
  | {
      intent: "reconcile-gap";
      resolution: "correct";
      correction: {
        correctedStopAt: string;
        correctedResumeAt: string;
      };
    };

export class FocusBlockOutcomeUnconfirmedError extends Error {
  override name = "FocusBlockOutcomeUnconfirmedError";
}

type FocusBlockPanelProps = {
  connectivity: FocusBlockConnectivity;
  monotonicNow?: () => number;
  onCommand: (command: FocusBlockUiCommand) => Promise<void>;
  repaintIntervalMs?: number;
  snapshot: FocusBlockSnapshot;
  timezone: string;
};

function formatDuration(durationMs: number) {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

function formatTime(value: string, timezone: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function localDateTimeValue(value: string, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(new Date(value))
    .reduce<Record<string, string>>((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function zonedLocalDateTimeToIso(value: string, timezone: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (match === null) return null;
  const [, year, month, day, hour, minute] = match;
  const localAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(new Date(localAsUtc))
    .reduce<Record<string, string>>((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});
  const representedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return new Date(localAsUtc - (representedAsUtc - localAsUtc)).toISOString();
}

function connectivityLabel(connectivity: FocusBlockConnectivity) {
  if (connectivity === "offline") {
    return "Offline · local-only timer estimate";
  }
  if (connectivity === "reconnecting") {
    return "Retrying live sync · local timer estimate from the last server snapshot";
  }
  if (connectivity === "pending") {
    return "Pending · waiting for the server to acknowledge this surface";
  }
  if (connectivity === "conflict") {
    return "Conflict · the authoritative snapshot must be reviewed";
  }
  return "Connected · server-anchored timer";
}

function pendingLabel(command: FocusBlockUiCommand | null) {
  if (command?.intent === "pause") return "Pausing…";
  if (command?.intent === "resume") return "Resuming…";
  if (command?.intent === "extend") return "Extending…";
  if (command?.intent === "reconcile-gap") return "Saving…";
  return null;
}

function acceptedLabel(command: FocusBlockUiCommand) {
  if (command.intent === "pause") return "Pause accepted.";
  if (command.intent === "resume") return "Resume accepted.";
  if (command.intent === "extend") {
    return `${command.extensionMinutes}-minute extension accepted.`;
  }
  if (command.resolution === "keep") return "Full interval accepted.";
  if (command.resolution === "unsure") return "Interval marked unsure.";
  return "Timer-running correction accepted.";
}

export function FocusBlockPanel({
  connectivity,
  monotonicNow = () => performance.now(),
  onCommand,
  repaintIntervalMs = 1_000,
  snapshot,
  timezone,
}: FocusBlockPanelProps) {
  const anchorKey = `${snapshot.id}:${snapshot.revision}:${snapshot.observedAt}`;
  const anchor = useRef<{
    key: string;
    monotonicTime: number;
    timerRunningDurationMs: number;
  } | null>(null);
  if (anchor.current === null || anchor.current.key !== anchorKey) {
    anchor.current = {
      key: anchorKey,
      monotonicTime: monotonicNow(),
      timerRunningDurationMs: snapshot.timerRunningDurationMs,
    };
  }
  const [, repaint] = useState(0);
  const [pending, setPending] = useState<FocusBlockUiCommand | null>(null);
  const [actionError, setActionError] = useState<{
    message: string;
    clearOnAuthoritativeSnapshot: boolean;
  } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [correctingGap, setCorrectingGap] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const gapHeading = useRef<HTMLHeadingElement>(null);
  const correctionInput = useRef<HTMLInputElement>(null);
  const suspiciousGapKey =
    snapshot.suspiciousGap === null
      ? null
      : `${snapshot.suspiciousGap.runningIntervalStartedAt}:${snapshot.suspiciousGap.detectedAt}`;

  useEffect(() => {
    if (snapshot.state !== "running") return;
    const interval = window.setInterval(
      () => repaint((value) => value + 1),
      repaintIntervalMs,
    );
    return () => window.clearInterval(interval);
  }, [repaintIntervalMs, snapshot.state]);

  useEffect(() => {
    setActionError((current) =>
      current?.clearOnAuthoritativeSnapshot ? null : current,
    );
  }, [anchorKey]);

  useEffect(() => {
    if (snapshot.suspiciousGap === null) return;
    gapHeading.current?.focus();
  }, [suspiciousGapKey]);

  useEffect(() => {
    if (correctingGap) correctionInput.current?.focus();
  }, [correctingGap]);

  const locallyProjectedMs =
    snapshot.state === "running"
      ? Math.max(0, monotonicNow() - anchor.current.monotonicTime)
      : 0;
  const displayedTimerRunningMs =
    anchor.current.timerRunningDurationMs + locallyProjectedMs;
  const controlsDisabled = pending !== null || connectivity !== "connected";
  const pendingText = pendingLabel(pending);

  async function execute(command: FocusBlockUiCommand) {
    setPending(command);
    setActionError(null);
    setAnnouncement("");
    try {
      await onCommand(command);
      setAnnouncement(acceptedLabel(command));
      if (command.intent === "reconcile-gap") setCorrectingGap(false);
    } catch (error) {
      setActionError({
        message:
          error instanceof Error
            ? error.message
            : "The Focus Block did not change. Try again.",
        clearOnAuthoritativeSnapshot:
          error instanceof FocusBlockOutcomeUnconfirmedError,
      });
    } finally {
      setPending(null);
    }
  }

  function submitCorrection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const correctedStopAt = zonedLocalDateTimeToIso(
      String(form.get("correctedStopAt")),
      timezone,
    );
    const correctedResumeAt = zonedLocalDateTimeToIso(
      String(form.get("correctedResumeAt")),
      timezone,
    );
    const gap = snapshot.suspiciousGap;
    if (
      gap === null ||
      correctedStopAt === null ||
      correctedResumeAt === null ||
      correctedStopAt < gap.runningIntervalStartedAt ||
      correctedResumeAt > gap.detectedAt ||
      correctedStopAt > correctedResumeAt
    ) {
      setCorrectionError(
        "Choose stop and resume times inside the factual timer-running interval. Resume cannot be before stop.",
      );
      correctionInput.current?.focus();
      return;
    }
    setCorrectionError(null);
    void execute({
      intent: "reconcile-gap",
      resolution: "correct",
      correction: { correctedStopAt, correctedResumeAt },
    });
  }

  return (
    <section className="focus-block-panel" aria-labelledby="focus-block-title">
      <div className="focus-block-heading">
        <div>
          <p className="status-label">Focus Block</p>
          <h3 id="focus-block-title">Timer</h3>
        </div>
        <span className={`focus-block-state state-${snapshot.state}`}>
          {snapshot.state === "running"
            ? "Running"
            : snapshot.state === "paused"
              ? "Paused"
              : "Ended"}
        </span>
      </div>
      <p
        className={`timer-connectivity connectivity-${connectivity}`}
        aria-live="polite"
      >
        {connectivityLabel(connectivity)}
      </p>
      <div className={`timer-face timer-face-${snapshot.state}`}>
        <span className="timer-indicator" aria-hidden="true" />
        <p className="timer-kicker">Timer-running time</p>
        <p className="timer-value" aria-label="Timer-running time">
          {formatDuration(displayedTimerRunningMs)}
        </p>
        <p className="timer-state-caption">
          {snapshot.state === "running"
            ? "Counting from the last confirmed server snapshot"
            : snapshot.state === "paused"
              ? "Paused at the confirmed elapsed time"
              : "Focus Block ended"}
        </p>
      </div>
      <p className="timer-description">
        This is timer-running time only. It records when the timer was running,
        not what happened during that time.
      </p>
      <p className="planned-time" aria-label="Planned checkpoint">
        <strong>Planned checkpoint:</strong> {snapshot.plannedMinimumMinutes}–
        {snapshot.plannedMaximumMinutes} minutes
        {snapshot.plannedExtensionMinutes > 0
          ? ` + ${snapshot.plannedExtensionMinutes} minutes extended`
          : ""}
      </p>

      {announcement === "" ? null : (
        <p className="focus-command-status" role="status" aria-live="polite">
          {announcement}
        </p>
      )}
      {actionError === null ? null : (
        <p className="focus-command-error" role="alert">
          {actionError.message}
        </p>
      )}

      {snapshot.state === "ended" ? null : (
        <div className="focus-controls" aria-label="Focus Block controls">
          <button
            type="button"
            disabled={controlsDisabled}
            onClick={() =>
              void execute({
                intent: snapshot.state === "running" ? "pause" : "resume",
              })
            }
          >
            {pending?.intent === "pause" || pending?.intent === "resume"
              ? pendingText
              : snapshot.state === "running"
                ? "Pause timer"
                : "Resume timer"}
          </button>
          <div className="extension-controls" aria-label="Extend checkpoint">
            {([5, 10, 15] as const).map((extensionMinutes) => (
              <button
                key={extensionMinutes}
                className="secondary-button"
                type="button"
                disabled={controlsDisabled}
                onClick={() =>
                  void execute({ intent: "extend", extensionMinutes })
                }
              >
                {pending?.intent === "extend" &&
                pending.extensionMinutes === extensionMinutes
                  ? pendingText
                  : `Extend by ${extensionMinutes} minutes`}
              </button>
            ))}
          </div>
        </div>
      )}

      {snapshot.suspiciousGap === null ? null : (
        <section
          className="gap-reconciliation"
          aria-labelledby="gap-reconciliation-title"
        >
          <p className="status-label">Reconciliation needed</p>
          <h4 id="gap-reconciliation-title" ref={gapHeading} tabIndex={-1}>
            Check this timer-running interval
          </h4>
          <p>
            The timer was running from{" "}
            {formatTime(
              snapshot.suspiciousGap.runningIntervalStartedAt,
              timezone,
            )}{" "}
            to {formatTime(snapshot.suspiciousGap.detectedAt, timezone)} (
            {formatDuration(snapshot.suspiciousGap.timerRunningDurationMs)}).
            The product cannot tell whether work continued during this entire
            interval.
          </p>
          <div className="gap-actions">
            <button
              type="button"
              disabled={controlsDisabled}
              onClick={() =>
                void execute({
                  intent: "reconcile-gap",
                  resolution: "keep",
                })
              }
            >
              Keep full interval
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={controlsDisabled}
              onClick={() => {
                setCorrectingGap(true);
                setCorrectionError(null);
              }}
            >
              Correct timer-running time
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={controlsDisabled}
              onClick={() =>
                void execute({
                  intent: "reconcile-gap",
                  resolution: "unsure",
                })
              }
            >
              Mark interval unsure
            </button>
          </div>
          {correctingGap ? (
            <form className="gap-correction-form" onSubmit={submitCorrection}>
              <label htmlFor={`corrected-stop-${snapshot.id}`}>
                Corrected stop time
              </label>
              <input
                id={`corrected-stop-${snapshot.id}`}
                ref={correctionInput}
                name="correctedStopAt"
                type="datetime-local"
                defaultValue={localDateTimeValue(
                  snapshot.suspiciousGap.runningIntervalStartedAt,
                  timezone,
                )}
                aria-describedby={
                  correctionError === null
                    ? undefined
                    : `correction-error-${snapshot.id}`
                }
              />
              <label htmlFor={`corrected-resume-${snapshot.id}`}>
                Corrected resume time
              </label>
              <input
                id={`corrected-resume-${snapshot.id}`}
                name="correctedResumeAt"
                type="datetime-local"
                defaultValue={localDateTimeValue(
                  snapshot.suspiciousGap.detectedAt,
                  timezone,
                )}
                aria-describedby={
                  correctionError === null
                    ? undefined
                    : `correction-error-${snapshot.id}`
                }
              />
              {correctionError === null ? null : (
                <p id={`correction-error-${snapshot.id}`} role="alert">
                  {correctionError}
                </p>
              )}
              <div className="gap-actions">
                <button type="submit" disabled={controlsDisabled}>
                  {pending?.intent === "reconcile-gap"
                    ? pendingText
                    : "Save correction"}
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  disabled={pending !== null}
                  onClick={() => setCorrectingGap(false)}
                >
                  Cancel correction
                </button>
              </div>
            </form>
          ) : null}
        </section>
      )}
    </section>
  );
}
